import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { clearToken, getToken, setCompanyId, setToken } from "@/lib/api";
import { rememberDevice } from "@/lib/trusted";
import { endImpersonation, forgetAdminReturn, isImpersonating } from "@/lib/impersonate";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      clearToken();
      setUser(false);
    });
  }, []);

  /** Password: { email, password }; PIN: { device_token, pin }. A password sign-in by a user with a PIN trusts this device. */
  const login = useCallback(async (creds, company) => {
    const { data } = await api.post("/auth/login", { ...creds, ...(company && { company }) });
    forgetAdminReturn();
    rememberDevice(data.device);
    setToken(data.access_token);
    setCompanyId(null);
    const me = (await api.get("/auth/me")).data;
    setUser(me);
    return me;
  }, []);

  const refreshUser = useCallback(() => api.get("/auth/me").then((r) => setUser(r.data)), []);

  const logout = useCallback(async () => {
    if (isImpersonating()) { await endImpersonation(); return; }
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* stateless logout */
    }
    clearToken();
    setUser(false);
  }, []);

  return <AuthContext.Provider value={{ user, login, logout, refreshUser }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
